import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Home, BedDouble, Bath, Users, Car, KeyRound, Pencil, Trash2, Heart, ListChecks, CalendarDays } from "lucide-react";
import PropertyForm from "@/components/PropertyForm";
import CalendarPanel from "@/components/CalendarPanel";

export default function PropertiesTab() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState(null);
  const [favs, setFavs] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bookFor, setBookFor] = useState(null);

  const load = async () => {
    const [p, f] = await Promise.all([api.get("/properties"), api.get("/favourites")]);
    setProperties(p.data.properties);
    setFavs(f.data.favourites);
    api.get("/properties/calendar/turnovers")
      .then((r) => setCalendars(r.data.properties))
      .catch(() => setCalendars([]));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/properties/${id}`); toast.success("Property removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (properties === null) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  const upcomingTurnovers = calendars.flatMap((c) =>
    (c.turnovers || []).filter((t) => !t.scheduled)
      .map((t) => ({ ...t, property_id: c.property_id, property_name: c.name })))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (adding || editing) {
    return (
      <div className="max-w-2xl">
        <PropertyForm property={editing} onCancel={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="properties-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Host mode · My properties</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Airbnb and holiday let turnovers, briefed properly in one tap.</p>
        </div>
        <button onClick={() => setAdding(true)} className="px-5 py-2.5 rounded-full bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2" data-testid="add-property-btn">
          <Plus className="h-4 w-4" /> Add property
        </button>
      </div>

      {upcomingTurnovers.length > 0 && (
        <div className="bg-card rounded-2xl border border-accent/30 p-5" data-testid="turnovers-to-schedule">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-accent" />
            <h4 className="font-display font-semibold text-sm">Turnovers to schedule</h4>
            <span className="ml-auto text-xs text-muted-foreground">From your Airbnb calendars · nothing is booked automatically</span>
          </div>
          <div className="mt-3 space-y-2">
            {upcomingTurnovers.slice(0, 5).map((t) => (
              <div key={`${t.property_id}-${t.date}`} className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-secondary/50 border border-border">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{new Date(t.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.property_name}</p>
                </div>
                {t.same_day_turnaround && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">Same-day</span>}
                <button onClick={() => navigate(favs[0] ? `/book/${favs[0].cleaner_id}?property=${t.property_id}&date=${t.date}&slot=morning` : `/search?property=${t.property_id}&date=${t.date}&slot=morning`)}
                  className="ml-auto px-4 py-1.5 rounded-full bg-accent text-white text-xs font-medium hover:bg-accent/90 transition-colors" data-testid={`schedule-top-${t.date}`}>
                  Schedule clean
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {properties.length === 0 ? (
        <div className="bg-card rounded-2xl border border-primary/5 p-10 text-center" data-testid="properties-empty">
          <Home className="h-8 w-8 text-accent mx-auto" />
          <p className="font-display font-semibold mt-3">No properties yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Add your let to save its address, cleaner notes, turnover checklist and contactless access details.
          </p>
          <button onClick={() => setAdding(true)} className="mt-5 px-6 py-2.5 rounded-full bg-accent text-white font-medium" data-testid="add-first-property">Add your first property</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {properties.map((p) => {
            const hasAccess = Object.values(p.access || {}).some(Boolean);
            return (
              <div key={p.property_id} className="bg-card rounded-2xl border border-primary/5 p-5" data-testid={`property-card-${p.property_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display font-semibold truncate">{p.name}</p>
                    <p className="text-sm text-muted-foreground">{p.address?.line1}, {p.address?.city} {p.address?.postcode}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid={`edit-property-${p.property_id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.property_id)} className="p-2 rounded-full hover:bg-secondary transition-colors text-muted-foreground hover:text-destructive" data-testid={`delete-property-${p.property_id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <Chip icon={BedDouble} text={`${p.bedrooms} bed`} />
                  <Chip icon={Bath} text={`${p.bathrooms} bath`} />
                  <Chip icon={Users} text={`Sleeps ${p.sleeps}`} />
                  {p.parking_info && <Chip icon={Car} text={p.parking_info} />}
                  <Chip icon={ListChecks} text={`${p.checklist?.length || 0} tasks`} />
                  {hasAccess && <Chip icon={KeyRound} text="Access saved" />}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => setBookFor(bookFor === p.property_id ? null : p.property_id)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors" data-testid={`book-property-${p.property_id}`}>
                    Book a clean
                  </button>
                </div>
                {bookFor === p.property_id && (                  <div className="mt-3 p-3 rounded-xl bg-secondary/50" data-testid={`preferred-panel-${p.property_id}`}>
                    <p className="text-sm font-medium flex items-center gap-1.5"><Heart className="h-4 w-4 text-accent" /> Your preferred cleaners</p>
                    {favs.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        You haven't favourited any cleaners yet — favourite the ones you trust and they'll appear here first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2 mt-2">
                        {favs.map((c) => (
                          <button key={c.cleaner_id} onClick={() => navigate(`/book/${c.cleaner_id}?property=${p.property_id}`)}
                            className="flex items-center gap-3 p-2 rounded-xl bg-background border border-border hover:border-accent transition-colors text-left" data-testid={`preferred-cleaner-${c.cleaner_id}`}>
                            <img src={c.photo_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
                            <span className="text-sm font-medium">{c.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">£{c.hourly_rate}/hr</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => navigate(`/search?property=${p.property_id}`)} className="text-sm text-accent font-medium mt-3" data-testid={`browse-cleaners-${p.property_id}`}>
                      Browse all cleaners instead
                    </button>
                  </div>
                )}
                <CalendarPanel property={p} favourites={favs} onChanged={load} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Chip({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-foreground"><Icon className="h-3.5 w-3.5 text-accent" /> {text}</span>;
}
